import { cleaningDisplayStatus, cleaningStatusBadgeClass } from "../cloudflare-access-workspace-sample/public/cleaning-display-status.js";
import { CLEANING_VIEWS, CLEANING_VIEW_LABELS, type CleaningView, type CleaningViewCounts } from "../shared/cleaning-views";

const CLEANING_VIEW_ICONS: Record<CleaningView, { color: string; icon: string }> = {
  received: { color: "green", icon: "▦" },
  arrived: { color: "blue", icon: "◆" },
  cleaning: { color: "violet", icon: "✦" },
  completed: { color: "green", icon: "✓" },
};

export interface CleaningManagementRecord {
  id: string;
  receiptNumber: string;
  confirmedOrderNumber: string;
  customerName: string;
  registrationDate: string;
  productName: string;
  wearingDate: string;
  arrivalDate: string;
  vendorRequestDate: string;
  processStatus: string;
  hasSentPaymentInvoice?: number;
  memo: string;
}

export interface CleaningManagementData {
  records: CleaningManagementRecord[];
  view: CleaningView;
  counts: CleaningViewCounts;
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
}

export interface CleaningWorkspaceOptions {
  view?: CleaningView;
  selectedIds?: ReadonlySet<string>;
  savingIds?: ReadonlySet<string>;
  requestSaving?: boolean;
}

export function bindCleaningSelection(root: HTMLElement, selectedIds: Set<string>, onChange: () => void): void {
  const inputs = Array.from(root.querySelectorAll<HTMLInputElement>("[data-cleaning-select]"));
  const all = root.querySelector<HTMLInputElement>("[data-cleaning-select-all]");
  const selectedCount = inputs.filter((input) => selectedIds.has(input.dataset.cleaningSelect ?? "")).length;
  if (all) {
    all.indeterminate = selectedCount > 0 && selectedCount < inputs.length;
    all.addEventListener("change", () => {
      for (const input of inputs) {
        const id = input.dataset.cleaningSelect ?? "";
        if (all.checked) selectedIds.add(id); else selectedIds.delete(id);
      }
      onChange();
    });
  }
  for (const input of inputs) input.addEventListener("change", () => {
    const id = input.dataset.cleaningSelect ?? "";
    if (input.checked) selectedIds.add(id); else selectedIds.delete(id);
    onChange();
  });
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

function displayText(value: string): string {
  return escapeHtml(value.trim() || "－");
}

function displayDate(value: string): string {
  const date = value.trim().match(/^\d{4}-\d{2}-\d{2}(?=$|[ T])/);
  return displayText(date ? date[0].replaceAll("-", "/") : value);
}

function receiptSummaryHtml(record: CleaningManagementRecord): string {
  const name = record.customerName.trim();
  const customer = name && !name.endsWith("様") ? `${name} 様` : name;
  const statusBadge = ["商品到着", "Draft済み", "決済完了"].includes(cleaningDisplayStatus(record)) ? ` ${cleaningStatusBadgeClass(record)}` : "";
  return `<div class="cleaning-receipt-summary">
    <strong class="cleaning-receipt-badge${statusBadge}">${displayText(record.receiptNumber)}</strong>
    <span class="cleaning-receipt-customer">${displayText(customer)}</span>
    <small class="cleaning-receipt-registered">${displayText(record.registrationDate)}</small>
  </div>`;
}

function rowClassAttribute(record: CleaningManagementRecord): string {
  if (["出荷依頼", "出荷依頼済"].includes(record.processStatus.trim())) return ' class="cleaning-shipping-completed"';
  if (cleaningDisplayStatus(record) === "決済待ち") return ' class="cleaning-payment-pending"';
  return "";
}

export function cleaningTabHtml(active: boolean, disabled: boolean): string {
  return `<button id="cleaning-tab" class="app-tab cleaning-tab${active ? " is-active" : ""}" type="button" role="tab" aria-selected="${active}" aria-controls="cleaning-panel" data-action="cleaning" ${disabled ? "disabled" : ""}>クリーニング</button>`;
}

export function cleaningWorkspaceHtml(
  data: CleaningManagementData | null,
  loading: boolean,
  error: string,
  options: CleaningWorkspaceOptions = {},
): string {
  const pagination = data?.pagination;
  const view = options.view ?? data?.view ?? "received";
  const selectedIds = options.selectedIds ?? new Set<string>();
  const savingIds = options.savingIds ?? new Set<string>();
  const busy = savingIds.size > 0 || Boolean(options.requestSaving);
  const requestDisabled = loading || Boolean(error) || busy;
  const actionsDisabled = requestDisabled || !data?.records.some((record) => selectedIds.has(record.id));
  const allSelected = Boolean(data?.records.length && data.records.every((record) => selectedIds.has(record.id)));
  const body = error
    ? `<div class="cleaning-message" role="alert"><strong>データを読み込めませんでした</strong><p>${escapeHtml(error)}</p></div>`
    : loading
      ? '<div class="cleaning-message" role="status"><span class="workspace-loading-spinner" aria-hidden="true"></span><strong>クリーニングデータを読み込んでいます…</strong></div>'
      : `<div class="cleaning-table-wrap" role="region" aria-label="クリーニング一覧" tabindex="0">
        <table class="cleaning-table">
          <colgroup><col class="cleaning-col-select"><col class="cleaning-col-receipt"><col class="cleaning-col-order"><col class="cleaning-col-product"><col class="cleaning-col-date"><col class="cleaning-col-date"><col class="cleaning-col-date"><col class="cleaning-col-status"><col class="cleaning-col-memo"></colgroup>
          <thead><tr><th scope="col" class="cleaning-select-cell"><input type="checkbox" data-cleaning-select-all aria-label="このページの受付をすべて選択" ${allSelected ? "checked" : ""} ${!data?.records.length || busy ? "disabled" : ""}></th><th scope="col">受付番号</th><th scope="col">注文番号</th><th scope="col">商品名</th><th scope="col">着用日</th><th scope="col">倉庫到着日</th><th scope="col">業者渡し日</th><th scope="col">処理状況</th><th scope="col">メモ</th></tr></thead>
          <tbody>${data?.records.length ? data.records.map((record) => `<tr${rowClassAttribute(record)}>
            <td class="cleaning-select-cell"><input type="checkbox" data-cleaning-select="${escapeHtml(record.id)}" aria-label="${displayText(record.receiptNumber)}を選択" ${selectedIds.has(record.id) ? "checked" : ""} ${busy ? "disabled" : ""}></td>
            <td>${receiptSummaryHtml(record)}</td>
            <td class="cleaning-order-number">${displayText(record.confirmedOrderNumber)}</td>
            <td class="cleaning-product-name">${displayText(record.productName)}</td>
            <td>${displayDate(record.wearingDate)}</td>
            <td><div class="cleaning-arrival"><span>${displayDate(record.arrivalDate)}</span><button type="button" class="button secondary cleaning-arrival-button" data-action="cleaning-arrive" data-cleaning-id="${escapeHtml(record.id)}" ${record.arrivalDate.trim() || busy ? "disabled" : ""}>${savingIds.has(record.id) ? "記録中…" : "到着"}</button></div></td>
            <td class="cleaning-vendor-request-date"><div class="cleaning-arrival"><span>${displayDate(record.vendorRequestDate)}</span><button type="button" class="button secondary cleaning-arrival-button" data-action="cleaning-handover" data-cleaning-id="${escapeHtml(record.id)}" ${!record.confirmedOrderNumber.trim() ? 'title="注文番号が登録されている場合のみ操作できます"' : ""} ${!record.confirmedOrderNumber.trim() || record.vendorRequestDate.trim() || busy ? "disabled" : ""}>${savingIds.has(record.id) ? "記録中…" : "渡し済"}</button></div></td>
            <td><span class="${cleaningStatusBadgeClass(record)}">${displayText(cleaningDisplayStatus(record))}</span></td>
            <td><div class="cleaning-memo-cell">
              ${record.memo.trim() ? `<button type="button" class="cleaning-memo-preview" data-action="cleaning-memo-view" data-cleaning-id="${escapeHtml(record.id)}" aria-label="メモの全文を表示" ${busy ? "disabled" : ""}>${escapeHtml(record.memo)}</button>` : '<span class="cleaning-memo-empty">－</span>'}
              <button type="button" class="button secondary cleaning-memo-button" data-action="cleaning-memo-edit" data-cleaning-id="${escapeHtml(record.id)}" ${busy ? "disabled" : ""}>メモ</button>
            </div></td>
          </tr>`).join("") : '<tr><td class="cleaning-empty" colspan="9">対象のクリーニング受付はありません。</td></tr>'}</tbody>
        </table>
      </div>`;
  return `<main id="cleaning-panel" class="etc-workspace-main cleaning-main" role="tabpanel" aria-labelledby="cleaning-tab" aria-busy="${loading}">
    <section class="cleaning-list" aria-labelledby="cleaning-workspace-title">
      <header class="cleaning-head">
        <div><p>CLEANING</p><h1 id="cleaning-workspace-title">クリーニング</h1></div>
        <div class="cleaning-head-actions">
          ${view === "arrived" ? `<button class="button cleaning-request" type="button" data-action="cleaning-request" ${requestDisabled ? "disabled" : ""}>${options.requestSaving ? "送信確認中…" : "クリーニング集荷連絡"}</button>` : ""}
          <button class="button repair-print" type="button" data-action="cleaning-print" title="処理内容は準備中です" disabled>🖨️印刷</button>
          ${view === "cleaning" ? `<button class="button ship" type="button" data-action="cleaning-ship" ${actionsDisabled ? "disabled" : ""}>${savingIds.size > 0 ? "記録中…" : "出荷依頼"}</button>` : ""}
          <span>${loading || error || !pagination ? "－" : pagination.totalItems}件</span>
          <button class="button secondary" type="button" data-action="cleaning-refresh" ${loading || busy ? "disabled" : ""}><span class="button-icon${loading ? " is-spinning" : ""}">↻</span>${loading ? "読込中…" : "更新"}</button>
        </div>
      </header>
      <nav class="cleaning-subtabs stats-grid" aria-label="クリーニングのサブタブ">
        ${CLEANING_VIEWS.map((tab) => `<button class="cleaning-subtab stat-card${view === tab ? " is-active" : ""}" type="button" data-action="cleaning-view" data-cleaning-view="${tab}" aria-pressed="${view === tab}" ${loading || busy ? "disabled" : ""}>
          <span class="stat-icon ${CLEANING_VIEW_ICONS[tab].color}" aria-hidden="true">${CLEANING_VIEW_ICONS[tab].icon}</span>
          <span><small>${CLEANING_VIEW_LABELS[tab]}</small><strong>${error || !data ? "－" : data.counts[tab]}</strong><em>件</em></span>
        </button>`).join("")}
      </nav>
      ${body}
      ${!loading && !error && pagination && pagination.totalPages > 1 ? `<nav class="cleaning-pagination" aria-label="クリーニング一覧のページ切替">
        <button class="button secondary" type="button" data-action="cleaning-previous" ${pagination.page <= 1 || busy ? "disabled" : ""}>前へ</button>
        <span>${pagination.page} / ${pagination.totalPages}ページ</span>
        <button class="button secondary" type="button" data-action="cleaning-next" ${pagination.page >= pagination.totalPages || busy ? "disabled" : ""}>次へ</button>
      </nav>` : ""}
    </section>
  </main>`;
}
